const MAX_AVATAR_URL_LENGTH = 2048;

export interface AvatarUrlValidation {
  value: string | null;
  error: string | null;
}

/**
 * The identity record stores an image URL, not uploaded bytes. Keep the
 * customer input bounded and refuse active/non-network schemes before it is
 * persisted and later rendered by every avatar in the portal.
 */
export function validateAvatarUrl(input: string): AvatarUrlValidation {
  const value = input.trim();
  if (value === "") return { value: null, error: null };
  if (value.length > MAX_AVATAR_URL_LENGTH) {
    return {
      value: null,
      error: `Image URL must be ${MAX_AVATAR_URL_LENGTH} characters or fewer.`,
    };
  }
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:") {
      return { value: null, error: "Use an HTTPS image URL." };
    }
    return { value: parsed.toString(), error: null };
  } catch {
    return { value: null, error: "Enter a valid HTTPS image URL." };
  }
}

/**
 * What the session's stored user agent actually says, and nothing more.
 *
 * The list is read to tell one's own browser from a script and a script from a
 * stolen login, so the difference it must carry is browser or program. The
 * portal used to print the agent unchanged under a heading that called it a
 * device, and `curl/8.7.1` and `Python-urllib/3.13` were shown to a customer as
 * devices they had signed in from (W8-UI-06). Naming a browser we do not
 * recognise was the older half of the same fault (X13): "Browser on unknown
 * device" filled both halves with a shrug.
 *
 * Nothing here is inferred beyond what the agent states. An agent this cannot
 * read is shown as it stands and is not called a device; a session that
 * recorded none says so.
 */
export type SessionClientKind = "browser" | "program" | "unknown";

export interface SessionClient {
  /** The line the row shows, in the design's two-part form. */
  label: string;
  kind: SessionClientKind;
}

/** A program that speaks HTTP, named the way its own agent names it. */
const PROGRAMS: [RegExp, string][] = [
  [/^curl\//i, "curl"],
  [/^Wget\//i, "Wget"],
  [/^Python-urllib\/|^python-requests\/|^httpx\//i, "a Python script"],
  [/^Go-http-client\//i, "a Go program"],
  [/^node\b|^undici\/|^axios\//i, "a Node script"],
  [/^Java\//i, "a Java program"],
  [/^PostmanRuntime\//i, "Postman"],
  [/^okhttp\//i, "an Android program"],
];

export function describeSessionClient(userAgent: string | null | undefined): SessionClient {
  const agent = (userAgent ?? "").trim();
  if (agent === "") return { label: "Client not recorded", kind: "unknown" };

  for (const [pattern, name] of PROGRAMS) {
    if (pattern.test(agent)) return { label: `${name} · a program, not a browser`, kind: "program" };
  }

  const browser = browserName(agent);
  if (browser === null) return { label: `${agent} · not recognised`, kind: "unknown" };
  const system = operatingSystemName(agent);
  return { label: system === null ? browser : `${browser} · ${system}`, kind: "browser" };
}

/**
 * Where the session was last seen from, as the server recorded it.
 *
 * An unspecified address — all zeros, in either family — is what the stack
 * writes when it resolved no origin at all. It is not a place the customer
 * signed in from, so it is reported as the absence it is.
 */
export function sessionOrigin(ipAddress: string | null | undefined): string {
  const address = (ipAddress ?? "").trim();
  if (address === "") return "Not recorded";
  const unspecified = address.replaceAll(":", "").replaceAll(".", "").replaceAll("0", "") === "";
  return unspecified ? "Not recorded" : address;
}

/** Edge and Chrome both claim Safari, and Edge claims Chrome — order matters. */
function browserName(userAgent: string): string | null {
  if (/Edg[A-Za-z]*\//.test(userAgent)) return "Edge";
  if (/OPR\//.test(userAgent)) return "Opera";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  if (/Chrome\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent)) return "Safari";
  return null;
}

function operatingSystemName(userAgent: string): string | null {
  if (/iPhone/.test(userAgent)) return "iPhone";
  if (/iPad/.test(userAgent)) return "iPad";
  if (/Android/.test(userAgent)) return "Android";
  if (/Windows/.test(userAgent)) return "Windows";
  if (/Macintosh|Mac OS X/.test(userAgent)) return "macOS";
  if (/CrOS/.test(userAgent)) return "ChromeOS";
  if (/Linux/.test(userAgent)) return "Linux";
  return null;
}
